import NavBar from "@components/NavBar";
import { useEffect, useState } from "react";
import { BsFillCreditCard2FrontFill } from "react-icons/bs";
import CustomHead from "../CustomHead";

import {
  AddNewPaymentMethod,
  PaymentMethodSelect,
  usePaymentMethods,
  usePlans,
  Plan,
  PaymentMethod,
  useCreateSubscription,
} from "@growthzilla/billingui";
import {
  CircularProgressIndicator,
  LoadingContainer,
} from "../CircularProgressIndicator";
import { MdAdd, MdArrowForward } from "react-icons/md";

import DialogComponent from "../Dialog";
import { useAuth } from "@hooks/useAuth";
import { Button } from "@components/ui/button";
import { DateTime } from "luxon";
import { OrganizationPlanStatus } from "types/index";
import useDialog from "@hooks/useDialog";
import { BiSolidOffer } from "react-icons/bi";
import SinglePlan from "../Plans/SinglePlan";
import parseJson from "@utils/parseJson";
import { PlanProperties } from "@hooks/usePlans/types";
import { useUserSubscriptions } from "@hooks/useUserSubscriptions";

type CurrencyType = "USD" | "EUR" | "NPR" | "AUD" | "INR" | "CAD" | "MXN";

function SubscriptionsSelect() {
  const [selectedPlan, setSelectedPlan] = useState<undefined | Plan>();

  const [selectPaymentDialog, setSelectPaymentDialog] = useState(false);
  const [addPaymentDialog, setAddPaymentDialog] = useState(false);

  const { plans, loading: plansLoading } = usePlans();
  const {
    paymentMethods,
    fetchPaymentMethods,
    loadingFetch: loadingPaymentMethods,
  } = usePaymentMethods();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    PaymentMethod | undefined
  >();

  const { create, loading: loadingCreateSub } = useCreateSubscription();
  const { organization } = useAuth();
  const user = organization?.profile

  const { openDialog, closeDialog } = useDialog();

  async function createSubscription(plan?: Plan, paymentMethodId?: number, { notify = true } = {}) {
    if (!plan || !user || !organization?.id) return;

    const _subStorageKey = "sub_" + user.id;
    localStorage.removeItem(_subStorageKey);

    const billing_start_date = DateTime.now()
      .plus({ month: paymentMethodId ? 3 : 1 })
      .toFormat(`yyyy-MM-dd`);

    const promoName: OrganizationPlanStatus | undefined = paymentMethodId
      ? "extended_free_trial"
      : "free_trial";

    create({
      promoName: promoName,
      currency: plan.currency as CurrencyType,
      paymentMethodId: paymentMethodId,
      businessId: organization.id,
      frequency: "Monthly",
      billing_start_date: billing_start_date,
      onSuccess() {
        window.location.reload();
      },
      userId: user.id,
      planId: plan?.id,
      notify,
    });
  }

  const { subLength } = useUserSubscriptions()

  useEffect(() => {
    if (!selectedPlan && plans.length > 0) {
      const _id = localStorage.getItem("selectedPlan");

      const _index = Math.max(
        plans.findIndex((i) => i.id.toString() === _id),
        0,
      );

      setSelectedPlan(plans[_index]);

      // create initial plan
      if (!plans[0] || selectedPlan || subLength > 0) return;
      createSubscription(plans[0], undefined, { notify: false });
    }
  }, [plans, plansLoading, selectedPlan]);

  return (
    <>
      <CustomHead title="Plans" />

      {
        // always show loading. hide this flow for now
        !plansLoading && false ? (
          <div className="flex min-h-screen w-screen flex-col overflow-x-hidden bg-background ">
            <div className="z-10">
              <NavBar />
            </div>
            <div className="z-0 mt-16 flex h-full w-full flex-col items-center justify-center gap-2">
              <h2 className="font-semibold text-accentColor dark:text-darkAccentColor">
                Pricing
              </h2>
              <h1 className="text-2xl">Predictable pricing, no surprises</h1>

              <div className="flex flex-wrap items-center justify-center gap-8 pb-2 pt-8">
                {plans.filter(p => {
                  const props = parseJson(p.properties) as PlanProperties
                  if (!props.is_admin_only) return true;
                  return user?.system_role === "admin"
                }).map((plan) => (
                  <SinglePlan
                    key={`plan_${plan.id}`}
                    plan={plan}
                    selectedPlan={selectedPlan}
                    actions={
                      <Button
                        onClick={() => {
                          setSelectedPlan(plan);
                          openDialog({
                            maxWidth: "600px",
                            title: "Select how you'd like to proceed",
                            customContent: (
                              <div className="grid grid-cols-2 gap-2 pt-4">
                                <TrialMethodButton
                                  label="I want to start my 1 month free trial"
                                  icon={<BiSolidOffer />}
                                  onClick={() => {
                                    closeDialog();
                                    createSubscription(plan, undefined);
                                  }}
                                />
                                <TrialMethodButton
                                  label="I want to add a payment method and get 3 months of free trial"
                                  icon={<BsFillCreditCard2FrontFill />}
                                  onClick={() => {
                                    closeDialog();
                                    if (paymentMethods.length === 0) {
                                      setAddPaymentDialog(true);
                                    } else {
                                      setSelectPaymentDialog(true);
                                    }
                                  }}
                                />
                              </div>
                            ),
                          });
                        }}
                        variant="filled"
                        size="sm"
                        className="w-full"
                      >
                        Start my free trial
                      </Button>
                    }
                  />
                ))}
              </div>

              <p className="mt-2 align-middle text-xs font-normal text-gray-600">
                * No credit card required
              </p>
            </div>
          </div>
        ) : (
          <div className="h-full w-full">
            <LoadingContainer />
          </div>
        )}

      <DialogComponent
        closeDialog={() => {
          setSelectPaymentDialog(false);
        }}
        isDialogOpen={selectPaymentDialog}
        dialogInfo={{
          title: "Select a payment Method",
          showActions: false,
          customContent: loadingPaymentMethods ? (
            <LoadingContainer />
          ) : (
            <PaymentMethodSelectorDialog
              selectedPaymentMethod={selectedPaymentMethod}
              onAddNewMethod={() => {
                setAddPaymentDialog(true);
              }}
              onSelected={(val) => {
                setSelectedPaymentMethod(val);
                setAddPaymentDialog(false);
                setSelectPaymentDialog(false);
                createSubscription(selectedPlan, val.id);
              }}
            />
          ),
        }}
      />

      <DialogComponent
        closeDialog={() => {
          setAddPaymentDialog(false);
        }}
        isDialogOpen={addPaymentDialog}
        dialogInfo={{
          title: "Add a new payment Method",
          maxWidth: "60vw",
          showActions: false,
          customContent: (
            <div className="py-16">
              <AddNewPaymentMethod
                hideTitle={true}
                showDialogOnAdd={false}
                onAdded={(val) => {
                  fetchPaymentMethods();
                  setSelectedPaymentMethod(val);
                  setAddPaymentDialog(false);

                  if (!selectPaymentDialog) {
                    createSubscription(selectedPlan, val.id);
                  }
                }}
              />
            </div>
          ),
        }}
      />

      <DialogComponent
        closeDialog={() => { }}
        isDialogOpen={loadingCreateSub}
        dialogInfo={{
          isDismissible: false,
          showActions: false,
          disableBG: true,
          customContent: (
            <div className="flex h-full w-full flex-col items-center justify-center">
              <CircularProgressIndicator />
            </div>
          ),
        }}
      />
    </>
  );
}

function PaymentMethodSelectorDialog({
  onSelected,
  onAddNewMethod,
  selectedPaymentMethod,
}: {
  onSelected: (method: PaymentMethod) => void;
  onAddNewMethod: () => void;
  selectedPaymentMethod?: PaymentMethod;
}) {
  const { paymentMethods } = usePaymentMethods();

  const [selectedPaymenthMethod, setSelectedPaymentMethod] = useState<
    undefined | number
  >(
    selectedPaymentMethod
      ? selectedPaymentMethod.id
      : paymentMethods.length > 0
        ? paymentMethods[0].id
        : undefined,
  );

  return (
    <div className="flex flex-col gap-4 pt-4">
      <PaymentMethodSelect
        paymentMethods={paymentMethods}
        selectedPaymentMethodId={selectedPaymenthMethod}
        onChange={(val) => {
          setSelectedPaymentMethod(val?.id);
        }}
      />
      <div className="flex items-center justify-between">
        <Button size="sm" onClick={onAddNewMethod}>
          <MdAdd />
          Add New Payment Method
        </Button>

        <Button
          size="sm"
          variant="filled"
          onClick={() => {
            const method = paymentMethods.find(
              (val) => val.id === selectedPaymenthMethod,
            );
            if (method) {
              onSelected(method);
            }
          }}
        >
          Proceed
          <MdArrowForward />
        </Button>
      </div>
    </div>
  );
}

function TrialMethodButton({
  label,
  icon,
  onClick,
}: {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="flex aspect-square w-full min-w-[250px] flex-col items-center justify-center gap-2 rounded-sm border bg-transparent transition-all hover:bg-primary/10 hover:text-primary"
      onClick={onClick}
    >
      <div className="text-8xl">{icon}</div>

      <p className="p-4 text-sm font-medium">{label}</p>
    </button>
  );
}
export default SubscriptionsSelect;